import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Presentation, Users2, Library, Trophy, Rocket, Sparkles, GraduationCap } from 'lucide-react';
import { getPlatformStats } from '../lib/api/fellowship';
import '../styles/statistics.css';

// The two live cards show the REAL count ("8,212"), not a rounded marketing
// badge ("8.2K+") — the number is genuine, so it is shown as-is.
const formatCount = (n) => Number(n || 0).toLocaleString('en-US');

// Shown only if /public/platform-stats is unreachable. These are the real
// production counts as of 27 Aug 2026 (the "+" says "at least"), never an
// invented figure — the old "4K+" placeholder undercounted by half and kept
// showing whenever the fetch failed.
const FALLBACK_LECTURES = '5,216+';
const FALLBACK_EDUCATORS = '8,212+';

const WorldMapVisualization = lazy(() => import('./WorldMapVisualization'));

const MapSkeleton = () => (
  <div
    style={{
      minHeight: '720px',
      background: '#fff',
      borderRadius: '20px',
    }}
    className="world-map-skeleton"
    aria-hidden="true"
  />
);

const Statistics = () => {
  // Live counts (real, from the backend) for the two cards that have real
  // data behind them. null until the fetch resolves, so the cards fall back
  // to the static marketing numbers below rather than flashing "0+" while
  // loading or if the request fails — a landing page must never show a
  // broken stats section to a visitor.
  const [liveCounts, setLiveCounts] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getPlatformStats()
      .then((data) => {
        if (cancelled) return;
        setLiveCounts({ lectures: data.lectures_generated ?? 0, educators: data.educators ?? 0 });
      })
      .catch(() => {}); // stays null -> static fallback numbers below
    return () => { cancelled = true; };
  }, []);

  const stats = [
    {
      icon: Presentation,
      color: '#3b82f6',
      number: liveCounts ? formatCount(liveCounts.lectures) : FALLBACK_LECTURES,
      label: 'Lectures Generated',
      description: 'During Beta Testing'
    },
    {
      icon: Users2,
      color: '#8b5cf6',
      number: liveCounts ? formatCount(liveCounts.educators) : FALLBACK_EDUCATORS,
      label: 'Educators',
      description: 'Teachers trust Teach Genie'
    },
    // Subject Categories stays static — no subject/category data model
    // exists in the backend yet (only a free-text "topic" per lesson), so
    // there's nothing real to count here.
    {
      icon: Library,
      color: '#f59e0b',
      number: '200+',
      label: 'Subject Categories',
      description: 'Comprehensive coverage'
    },
    {
      icon: Trophy,
      color: '#10b981',
      number: '99.9%',
      label: 'Tutor Satisfaction',
      description: ''
    }
  ];

  return (
    <section className="statistics">
      <div className="container">


        {/* World Map Section */}
        <div className="world-map-section">
          <div className="statistics-header reveal-down">
            <h2>
              Global Educator Network
            </h2>
            <p>Connecting teachers across continents</p>
          </div>

          <div className="world-map-container reveal">
            <Suspense fallback={<MapSkeleton />}>
              <WorldMapVisualization />
            </Suspense>
          </div>
        </div>

        <div className="statistics-header reveal-down">
          <h2 className="reveal-text">
            Trusted by Educators Worldwide
          </h2>
          <p>See why thousands of teachers choose Teach Genie™ for lesson planning</p>
        </div>

        <div className="stats-grid">
          {stats.map((stat, index) => (
            <div key={index} className={index < 2 ? 'reveal-left' : 'reveal-right'} style={{ transitionDelay: `${index * 0.1}s` }}>
              <div className="stat-card">
                <div className="stat-icon-wrapper" style={{ background: `linear-gradient(135deg, ${stat.color}15, ${stat.color}30)` }}>
                  <stat.icon size={40} strokeWidth={2.5} style={{ color: stat.color }} />
                </div>
                <div className="stat-number">{stat.number}</div>
                <h3 className="stat-label">{stat.label}</h3>
                <p className="stat-description">{stat.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="statistics-highlight reveal-stagger">
          <div className="highlight-item" style={{ transitionDelay: '0.1s' }}>
            <div className="highlight-icon-wrapper">
              <Rocket className="highlight-icon" size={40} strokeWidth={2.5} />
            </div>
            <h3>Lightning Fast</h3>
            <p>Get professional materials in under 2 minutes</p>
          </div>
          <div className="highlight-item" style={{ transitionDelay: '0.2s' }}>
            <div className="highlight-icon-wrapper">
              <Sparkles className="highlight-icon" size={40} strokeWidth={2.5} />
            </div>
            <h3>AI-Powered Discovery</h3>
            <p>Agents search thousands of academic resources</p>
          </div>
          <div className="highlight-item" style={{ transitionDelay: '0.3s' }}>
            <div className="highlight-icon-wrapper">
              <GraduationCap className="highlight-icon" size={40} strokeWidth={2.5} />
            </div>
            <h3>All Levels</h3>
            <p>From school to postgraduate education</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Statistics;
